/** German date formatter — 2-digit day/month/year per the prototype ("04.04.2026"). */
const DATE = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

/** Long German date ("4. April 2026") — for headers and detail views. */
const DATE_LONG = new Intl.DateTimeFormat("de-DE", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

export function formatDateDE(input: Date | string): string {
  const d = typeof input === "string" ? new Date(input) : input;
  return DATE.format(d);
}

export function formatDateLongDE(input: Date | string): string {
  const d = typeof input === "string" ? new Date(input) : input;
  return DATE_LONG.format(d);
}

/* Career and education periods (#149). Scrapers store dates as free text:
 * „2022-03-01" (the day is padding), „2022-03", „2012", empty, or a word for a
 * current job. Parsed by pattern, never with `new Date()`, which shifts by
 * timezone and invents a day. */

/** Words the scrapers store for a Laufende Stelle, matched in any case. An empty end
 * isn't one: it means the role ended at an unknown time (#157, GLOSSARY.md). */
const PRESENT_WORDS: ReadonlySet<string> = new Set([
  "present",
  "heute",
  "bis heute",
  "aktuell",
  "now",
  "current",
]);
const MIN_MONTH = 1;
const MAX_MONTH = 12;

const MONTH_DATE = /^(\d{4})-(\d{2})(?:-\d{2})?$/;
const YEAR_ONLY = /^\d{4}$/;

export interface PeriodDate {
  text: string;
  /** For `<time dateTime>`, when the date was recognised. */
  dateTime?: string;
  /** What screen readers hear instead of `text` (e.g. „bis" spelled out). */
  spoken?: string;
}

/** The end of a role that ended at an unknown time (#157). */
const UNKNOWN_END: PeriodDate = { text: "unbekannt" };

/** A single date (`from` only), a range, or a start or end on its own („ab"/„bis"). */
export interface Period {
  from?: PeriodDate;
  to?: PeriodDate;
  lead?: "ab" | "bis";
}

export interface PeriodOptions {
  /** What a start without an end means: a role that ended at an unknown time
   * („03/2022 – unbekannt", career, #157, #163) or something still going on („ab 2022", education). */
  openEnd?: "unknown" | "ongoing";
}

type Parsed = PeriodDate | "present" | null;

function parsePeriodDate(value: string | null | undefined): Parsed {
  const v = (value ?? "").trim();
  if (!v) return null;
  if (PRESENT_WORDS.has(v.toLowerCase())) return "present";
  const month = MONTH_DATE.exec(v);
  const monthNumber = Number(month?.[2]);
  if (month && monthNumber >= MIN_MONTH && monthNumber <= MAX_MONTH)
    return { text: `${month[2]}/${month[1]}`, dateTime: `${month[1]}-${month[2]}` };
  if (YEAR_ONLY.test(v)) return { text: v, dateTime: v };
  return { text: v };
}

export function periodParts(
  from: string | null | undefined,
  to: string | null | undefined,
  { openEnd = "unknown" }: PeriodOptions = {},
): Period | null {
  const start = parsePeriodDate(from);
  const end = parsePeriodDate(to);
  // A start that is itself „now" says no more than „aktuell", whatever the end.
  if (start === "present" && (end === "present" || !end)) return { from: { text: "aktuell" } };
  const startDate = start === "present" ? { text: "heute" } : start;
  if (!startDate && !end) return null;
  if (!startDate)
    return end === "present"
      ? { from: { text: "aktuell" } }
      : { lead: "bis", to: end as PeriodDate };
  if (!end)
    return openEnd === "ongoing"
      ? { lead: "ab", from: startDate }
      : { from: startDate, to: UNKNOWN_END };
  if (end === "present") return { from: startDate, to: { text: "heute" } };
  if (end.text === startDate.text) return { from: startDate };
  return { from: startDate, to: end };
}

/** The period as one line of text („03/2022 – heute", „ab 03/2022"), or "" when there's none. */
export function formatPeriod(
  from: string | null | undefined,
  to: string | null | undefined,
  options: PeriodOptions = {},
): string {
  const p = periodParts(from, to, options);
  if (!p) return "";
  if (p.lead === "ab") return `ab ${p.from?.text ?? ""}`;
  if (p.lead === "bis") return `bis ${p.to?.text ?? ""}`;
  return p.to ? `${p.from?.text ?? ""} – ${p.to.text}` : (p.from?.text ?? "");
}
